<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left"><h1 class="page-title">{{ isEdit ? '編輯設計師' : '新增設計師' }}</h1></div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="saving" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading || saving" @click="save">
          {{ saving ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>載入中...</p></div>
      <form v-else class="form-container salon-form-page" @submit.prevent="save">
        <div class="form-section">
          <h2 class="section-title">基本資料</h2>
          <div class="profile-layout">
            <ImageUploader
              v-model="form.avatarUrl"
              :upload-fn="uploadAvatar"
              shape="square"
              placeholder="上傳大頭貼"
              hint="JPG / PNG / WebP"
              size-hint="建議尺寸 800×800px，最大 5MB"
            />
            <div class="form-grid">
            <div class="form-group">
              <label class="form-label required-label">設計師代碼</label>
              <input v-model.trim="form.stylistCode" class="form-control" maxlength="50" :disabled="isEdit" required />
              <small v-if="isEdit" class="form-text">設計師代碼建立後無法修改</small>
            </div>
            <div class="form-group">
              <label class="form-label required-label">內部姓名</label>
              <input v-model.trim="form.internalName" class="form-control" maxlength="100" required />
            </div>
            <div class="form-group">
              <label class="form-label required-label">顯示名稱</label>
              <input v-model.trim="form.displayName" class="form-control" maxlength="100" required />
            </div>
            <div class="form-group">
              <label class="form-label">職稱</label>
              <input v-model.trim="form.title" class="form-control" maxlength="100" />
            </div>
            <div class="form-group">
              <label class="form-label">年資</label>
              <input v-model.number="form.yearsOfExperience" type="number" class="form-control" min="0" />
            </div>
            <div class="form-group">
              <label class="form-label">同時服務上限（覆蓋門市設定）</label>
              <input
                v-model.number="form.defaultConcurrencyLimit"
                type="number"
                class="form-control"
                min="1"
                max="20"
                placeholder="依門市設定"
              />
              <small class="form-text">留空時使用各門市設定的預設上限。</small>
            </div>
            <div class="form-group">
              <label class="form-label">排序</label>
              <input v-model.number="form.sortOrder" type="number" class="form-control" />
            </div>
            <div class="form-group">
              <label class="form-label">狀態</label>
              <select v-model="form.status" class="form-control">
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
            </div>
            <label class="check-label">
              <input v-model="form.isBookable" type="checkbox" /> 開放預約
            </label>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">個人簡介</label>
            <textarea v-model.trim="form.bio" class="form-control" rows="4"></textarea>
          </div>
        </div>

        <div class="form-section">
          <h2 class="section-title">預約通知聯絡資料</h2>
          <p class="form-text">門市啟用新預約通知後，系統會依門市設定的管道順序通知此設計師。</p>
          <div class="form-grid contact-grid">
            <div class="form-group">
              <label class="form-label">行動電話</label>
              <input
                v-model.trim="form.mobilePhone"
                type="tel"
                class="form-control"
                maxlength="30"
                placeholder="0912345678"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Email</label>
              <input
                v-model.trim="form.email"
                type="email"
                class="form-control"
                maxlength="255"
                placeholder="stylist@example.com"
              />
            </div>
          </div>
          <div class="form-group line-friend-field">
            <label class="form-label">關聯 LINE 好友</label>
            <LineFriendPicker
              :model-value="selectedLineFollow?.lineUserId || ''"
              :friend="selectedLineFollow"
              @select="selectLineFollow"
            />
          </div>
        </div>

        <div class="form-section">
          <h2 class="section-title">可服務門市</h2>
          <label v-for="store in stores" :key="store.id" class="check-label">
            <input v-model="form.storeIds" type="checkbox" :value="store.id" />
            {{ store.storeName }}
          </label>
        </div>

        <div class="form-section">
          <h2 class="section-title">可提供服務</h2>
          <label v-for="item in services" :key="item.id" class="check-label">
            <input v-model="form.serviceIds" type="checkbox" :value="item.id" />
            {{ item.name }}
          </label>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue';
import LineFriendPicker from '@/components/LineFriendPicker.vue';
import { salonService } from '@/api/modules/salon.js';
import { storeService } from '@/api/modules/stores.js';
import { showError, showSuccess } from '@/utils/swal.js';

const emptyForm = () => ({
  stylistCode: '', internalName: '', displayName: '', title: '', bio: '',
  avatarUrl: null, mobilePhone: '', email: '', lineFollowId: null,
  yearsOfExperience: 0, defaultConcurrencyLimit: null, sortOrder: 0,
  status: 'active', isBookable: true, storeIds: [], serviceIds: []
});

export default {
  name: 'SalonStylistForm',
  components: { ImageUploader, LineFriendPicker },
  data() {
    return {
      loading: false,
      saving: false,
      stores: [],
      services: [],
      originalStores: [],
      selectedLineFollow: null,
      form: emptyForm()
    };
  },
  computed: {
    isEdit() { return Boolean(this.$route.params.id); }
  },
  async mounted() {
    this.loading = true;
    try {
      const [stores, services] = await Promise.all([
        storeService.getStores({ page: 1, pageSize: 100, status: 'active' }),
        salonService.listServices({ page: 1, pageSize: 100, status: 'active' })
      ]);
      this.stores = stores?.data?.items || [];
      this.services = services?.data?.items || [];
      if (this.isEdit) await this.loadStylist();
    } catch (error) {
      await this.handleError(error, '設計師資料載入失敗');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadStylist() {
      const response = await salonService.getStylist(this.$route.params.id);
      const item = response.data;
      this.originalStores = item.stores || [];
      this.selectedLineFollow = item.lineFollow || null;
      this.form = {
        ...emptyForm(),
        ...item,
        title: item.title || '',
        bio: item.bio || '',
        avatarUrl: item.avatarUrl || null,
        mobilePhone: item.mobilePhone || '',
        email: item.email || '',
        lineFollowId: item.lineFollowId || null,
        yearsOfExperience: Number(item.yearsOfExperience || 0),
        defaultConcurrencyLimit: item.defaultConcurrencyLimit == null
          ? null
          : Number(item.defaultConcurrencyLimit),
        sortOrder: Number(item.sortOrder || 0),
        storeIds: (item.stores || []).map(store => store.storeId),
        serviceIds: item.serviceIds || []
      };
    },
    async save() {
      if (this.saving) return;
      const concurrencyLimit = this.form.defaultConcurrencyLimit;
      if (concurrencyLimit !== null && concurrencyLimit !== '') {
        const value = Number(concurrencyLimit);
        if (!Number.isInteger(value) || value < 1 || value > 20) {
          await showError('驗證失敗', '同時服務上限覆蓋值必須是 1 到 20 的整數，或留空使用門市設定。');
          return;
        }
      }
      this.saving = true;
      try {
        const body = {
          internalName: this.form.internalName,
          displayName: this.form.displayName,
          title: this.form.title || null,
          bio: this.form.bio || null,
          avatarUrl: this.form.avatarUrl || null,
          mobilePhone: this.form.mobilePhone || null,
          email: this.form.email || null,
          lineFollowId: this.form.lineFollowId || null,
          yearsOfExperience: Number(this.form.yearsOfExperience),
          defaultConcurrencyLimit: this.form.defaultConcurrencyLimit == null
            || this.form.defaultConcurrencyLimit === ''
            ? null
            : Number(this.form.defaultConcurrencyLimit),
          sortOrder: Number(this.form.sortOrder),
          status: this.form.status,
          isBookable: this.form.isBookable,
          stores: this.form.storeIds.map((storeId, index) => {
            const current = this.originalStores.find(store => store.storeId === storeId);
            return {
              storeId,
              concurrencyLimit: current?.concurrencyLimit || null,
              isBookable: current?.isBookable ?? true,
              sortOrder: current?.sortOrder ?? index
            };
          }),
          serviceIds: this.form.serviceIds
        };
        if (this.isEdit) await salonService.updateStylist(this.$route.params.id, body);
        else await salonService.createStylist({ ...body, stylistCode: this.form.stylistCode });
        await showSuccess(this.isEdit ? '更新成功' : '建立成功', '設計師資料已儲存');
        this.goBack();
      } catch (error) {
        await this.handleError(error, '設計師資料儲存失敗');
      } finally {
        this.saving = false;
      }
    },
    uploadAvatar(file) {
      return salonService.uploadStylistAvatar(file);
    },
    selectLineFollow(friend) {
      this.selectedLineFollow = friend;
      this.form.lineFollowId = friend?.id || null;
    },
    goBack() { this.$router.push({ name: 'SalonStylists' }); },
    async handleError(error, fallback) {
      await showError(fallback, error.response?.data?.message || error.message || fallback);
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';
.salon-form-page { max-width: 1000px; margin: 0 auto; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.profile-layout { align-items: start; display: grid; gap: 24px; grid-template-columns: 220px 1fr; }
.profile-layout .form-grid { width: 100%; }
.check-label { display: inline-flex; align-items: center; gap: 6px; margin: 6px 18px 6px 0; }
.form-text { color: #64748b; display: block; margin-top: 6px; }
.contact-grid { margin-top: 16px; }
.line-friend-field { margin-top: 18px; }
@media (max-width: 768px) {
  .form-grid, .profile-layout { grid-template-columns: 1fr; }
}
</style>
